import React, { useMemo } from "react";

import { Fade } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import { FartherManagedAccount } from "@fartherfinance/frontend/api/Accounts/Types";
import { ExternalAccountExtended } from "@fartherfinance/frontend/api/ExternalAccount/hooks/useExternalAccountsExtended";
import { BalanceInfo } from "@fartherfinance/frontend/api/PerformanceGroups/hooks/useAllAccountBalances";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import { TransfersV2Path } from "@src/config/routing/RouterPaths";
import AlertInline from "@src/sharedComponents/AlertInline/AlertInline";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";

import { CreateTransferFormData, transferTypes } from "./const";
import CreateTransferDepositForm from "./forms/CreateTransferDepositForm";
import CreateTransferInvestmentForm from "./forms/CreateTransferInvestmentForm";
import CreateTransferIRAForm from "./forms/CreateTransferIRAForm";
import CreateTransferWithdrawalForm from "./forms/CreateTransferWithdrawalForm";
import { isPlaidAccount } from "./utils";

import styles from "./CreateTransferForm.module.css";

interface CreateTransferFormProps {
  fundingAccount: ExternalAccountExtended | null | undefined;
  managedAccounts: FartherManagedAccount[];
  allAccountBalances: BalanceInfo[];
  statementFile: File | null;
  setStatementFile: (file: File | null) => void;
}

const CreateTransferForm: React.FC<CreateTransferFormProps> = ({
  fundingAccount,
  managedAccounts,
  allAccountBalances,
  setStatementFile,
  statementFile,
}) => {
  const { enableOneTimeAch, enableOneTimeAcats } = useFlags();
  const form = useFormContext<CreateTransferFormData>();
  const navigate = useNavigate();
  const { clientId } = useParams<{
    clientId: ClientId;
  }>();

  const transferTypeOptions = useMemo(() => {
    const filteredTransferTypes = transferTypes.filter((type) => {
      if (type === "Deposit" || type === "Withdrawal") {
        return enableOneTimeAch;
      }

      if (type === "Investment" || type === "IRA") {
        return enableOneTimeAcats;
      }

      return true;
    });

    return filteredTransferTypes.map((option) => ({
      label: option,
      value: option,
    }));
  }, [enableOneTimeAch, enableOneTimeAcats]);

  const transferType = form.watch("transferType");
  const isDeposit = transferType?.value === "Deposit";
  const isWithdrawal = transferType?.value === "Withdrawal";
  const isInvestment = transferType?.value === "Investment";
  const isIRA = transferType?.value === "IRA";

  const fundingAccountHealth =
    fundingAccount && isPlaidAccount(fundingAccount)
      ? fundingAccount.details.account.connectionHealthType
      : null;

  const fundingStatus = fundingAccount?.fundingDetails?.fundingStatus;

  return (
    <div className={styles.container}>
      {isDeposit && fundingAccountHealth === "unavailable" && (
        <Fade in>
          <AlertInline
            severity="info"
            message="Unable to retrieve the latest balance from your bank. Please  ensure sufficient funds are available before making a deposit."
          />
        </Fade>
      )}

      {fundingStatus === "pending" && (
        <Fade in>
          <AlertInline
            severity="info"
            message="Your bank account authorization is pending. Please check again later."
          />
        </Fade>
      )}

      {(isDeposit || isWithdrawal) && fundingAccount === null && (
        <Fade in>
          <AlertInline
            severity="error"
            message="Set up funding account now"
            onClick={() =>
              navigate(
                `/Client/${clientId}/${TransfersV2Path}/ChangeFundingAccount`
              )
            }
          />
        </Fade>
      )}

      <FormDropdownField
        name="transferType"
        label="Transfer Type"
        disableSearch
        values={transferTypeOptions}
        required="Please select a transfer type"
      />

      {isDeposit && (
        <CreateTransferDepositForm
          fundingAccount={fundingAccount}
          managedAccounts={managedAccounts}
          allAccountBalances={allAccountBalances}
        />
      )}

      {isWithdrawal && (
        <CreateTransferWithdrawalForm
          fundingAccount={fundingAccount}
          managedAccounts={managedAccounts}
          allAccountBalances={allAccountBalances}
        />
      )}

      {isInvestment && (
        <CreateTransferInvestmentForm
          managedAccounts={managedAccounts}
          setStatementFile={setStatementFile}
          statementFile={statementFile}
        />
      )}

      {isIRA && (
        <CreateTransferIRAForm
          managedAccounts={managedAccounts}
          setStatementFile={setStatementFile}
          statementFile={statementFile}
        />
      )}
    </div>
  );
};

export default CreateTransferForm;
